// src/pages/admin/AdminBlogs.jsx
import React, { useEffect, useState, lazy, Suspense } from "react";
import { useForm } from "react-hook-form";
import { getBlogs, addBlog, updateBlog, deleteBlog } from "../../services/firestoreService";
import Modal from "../../components/common/Modal";
import ImageUploader from "../../components/common/ImageUploader";
import Spinner from "../../components/common/Spinner";
import toast from "react-hot-toast";
import { format } from "date-fns";
import { FiPlus, FiEdit2, FiTrash2, FiEye, FiEyeOff } from "react-icons/fi";
import "react-quill/dist/quill.snow.css";

// Lazy load react-quill to avoid SSR issues
const ReactQuill = lazy(() => import("react-quill"));

export default function AdminBlogs() {
  const [blogs,     setBlogs]     = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing,   setEditing]   = useState(null);
  const [image,     setImage]     = useState("");
  const [content,   setContent]   = useState("");
  const [saving,    setSaving]    = useState(false);

  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  const load = async () => {
    const b = await getBlogs(false);
    setBlogs(b);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openAdd = () => {
    setEditing(null); setImage(""); setContent("");
    reset({ title: "", excerpt: "", author: "", category: "", published: false });
    setModalOpen(true);
  };
  const openEdit = (blog) => {
    setEditing(blog); setImage(blog.image || ""); setContent(blog.content || "");
    reset({ title: blog.title, excerpt: blog.excerpt || "", author: blog.author || "", category: blog.category || "", published: blog.published || false });
    setModalOpen(true);
  };

  const onSubmit = async (data) => {
    if (!content.trim()) { toast.error("Blog content is required"); return; }
    setSaving(true);
    try {
      const payload = { ...data, content, image, published: data.published === true || data.published === "true" };
      if (editing) { await updateBlog(editing.id, payload); toast.success("Blog updated"); }
      else         { await addBlog(payload); toast.success("Blog created"); }
      setModalOpen(false);
      load();
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete "${title}"?`)) return;
    await deleteBlog(id);
    toast.success("Blog deleted");
    setBlogs((prev) => prev.filter((b) => b.id !== id));
  };

  const togglePublish = async (blog) => {
    await updateBlog(blog.id, { published: !blog.published });
    setBlogs((prev) => prev.map((b) => b.id === blog.id ? { ...b, published: !b.published } : b));
    toast.success(blog.published ? "Blog unpublished" : "Blog published");
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Blogs</h1>
        <button onClick={openAdd} className="btn-primary text-sm sm:text-md flex items-center gap-2">
          <FiPlus className="w-4 h-4" /> New Blog
        </button>
      </div>

      {loading ? <Spinner center /> : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {["Image", "Title", "Author", "Category", "Date", "Published", "Actions"].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {blogs.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-12 text-gray-500">No blogs yet. Create your first post!</td></tr>
              ) : (
                blogs.map((blog) => (
                  <tr key={blog.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="w-14 h-10 rounded-lg overflow-hidden bg-gray-100">
                        {blog.image ? <img src={blog.image} alt={blog.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-lg">📰</div>}
                      </div>
                    </td>
                    <td className="px-4 py-3 max-w-xs">
                      <p className="font-medium text-gray-900 truncate">{blog.title}</p>
                      <p className="text-xs text-gray-400 truncate">{blog.excerpt}</p>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{blog.author || "—"}</td>
                    <td className="px-4 py-3 text-gray-600">{blog.category || "—"}</td>
                    <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                      {blog.createdAt?.toDate ? format(blog.createdAt.toDate(), "dd MMM yyyy") : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <button onClick={() => togglePublish(blog)} className={`badge cursor-pointer ${blog.published ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>
                        {blog.published ? "Published" : "Draft"}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button onClick={() => openEdit(blog)} className="p-1.5 rounded-lg hover:bg-primary-50 text-primary-600 transition-colors"><FiEdit2 className="w-4 h-4" /></button>
                        <button onClick={() => handleDelete(blog.id, blog.title)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-500 transition-colors"><FiTrash2 className="w-4 h-4" /></button>
                        <button onClick={() => togglePublish(blog)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-600 transition-colors">
                          {blog.published ? <FiEyeOff className="w-4 h-4" /> : <FiEye className="w-4 h-4" />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          </div>
        </div>
      )}

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={editing ? "Edit Blog" : "New Blog Post"} size="xl">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label className="label">Title *</label>
            <input {...register("title", { required: "Title is required" })} className={`input ${errors.title ? "border-red-400" : ""}`} placeholder="Blog post title" />
            {errors.title && <p className="text-xs text-red-500 mt-1">{errors.title.message}</p>}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Author</label>
              <input {...register("author")} className="input" placeholder="Author name" />
            </div>
            <div>
              <label className="label">Category</label>
              <input {...register("category")} className="input" placeholder="e.g. Farming Tips" />
            </div>
          </div>
          <div>
            <label className="label">Excerpt (short preview)</label>
            <textarea {...register("excerpt")} rows={2} className="input resize-none" placeholder="Short description shown in blog listing..." />
          </div>
          <div>
            <label className="label">Content *</label>
            <Suspense fallback={<div className="h-40 bg-gray-100 rounded-lg animate-pulse" />}>
              <ReactQuill
                value={content}
                onChange={setContent}
                theme="snow"
                placeholder="Write your blog content here..."
                modules={{
                  toolbar: [
                    [{ header: [1, 2, 3, false] }],
                    ["bold", "italic", "underline", "strike"],
                    [{ list: "ordered" }, { list: "bullet" }],
                    ["blockquote", "link"],
                    ["clean"],
                  ],
                }}
              />
            </Suspense>
          </div>
          <div>
            <label className="label">Featured Image</label>
            <ImageUploader folder="blogs" onUpload={(imgs) => setImage(imgs[0] || "")} existingImages={image ? [image] : []} maxFiles={1} />
          </div>
          <div className="flex items-center gap-3">
            <input {...register("published")} type="checkbox" id="pub" className="w-4 h-4 accent-primary-600" />
            <label htmlFor="pub" className="text-sm text-gray-700 font-medium">Publish immediately</label>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={saving} className="btn-primary flex-1 flex items-center justify-center gap-2">
              {saving ? <><Spinner size="sm" /> Saving...</> : editing ? "Update Blog" : "Create Blog"}
            </button>
            <button type="button" onClick={() => setModalOpen(false)} className="btn-secondary">Cancel</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}